import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/lib/use-auth';
import { useUserOptions } from '@/lib/use-user-options';
import { denialReason } from '@/lib/permissions';
import type {
  EnquiryAssignee,
  EnquiryAssigneeCreateInput,
  EnquiryAssigneeUpdateInput,
} from '@/lib/types';
import {
  Badge,
  Button,
  Combobox,
  ConfirmDialog,
  Field,
  Input,
  Panel,
  PanelBody,
  PanelHeader,
  Skeleton,
  type ComboboxOption,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { EmailLink } from '@/components/ui/TextLink';

/*
 * THE PEOPLE AN ENQUIRY CAN BE ASSIGNED TO — `/enquiry-assignees`.
 *
 * Free text (name, email, optional team), deliberately NOT tied to an EMERGE
 * user (owner decision). The "From EMERGE users" picker only COPIES a staff
 * member's name and email into the add form; nothing links the two.
 *
 * ## The read is open, the writes are `settings-notifications`
 *
 * `GET /enquiry-assignees` needs no grant (the assign modal reads it for any
 * triager), so the list renders for every reader. Add, edit, remove and
 * restore gate on `settings-notifications.update`; the server's 403 is still
 * handled, because a grant can change under a live access token.
 *
 * ## ⛔ There is no hard delete
 *
 * "Remove" is `PATCH {isActive: false}`. Enquiries already assigned keep a
 * snapshot of the name and still render; the person stops being offered.
 * Removed people stay on this list with a "Removed" badge and a Restore action.
 *
 * ## The list is re-read after every write
 *
 * One small unpaginated GET, and it is the server's order (`sort_order, name`)
 * and its normalised values — never a locally patched copy.
 */

const FIELD_KEYS = ['name', 'email', 'team'] as const;
type FieldKey = (typeof FIELD_KEYS)[number];
type FieldErrors = Partial<Record<FieldKey, string>>;

interface Draft {
  name: string;
  email: string;
  team: string;
}

const EMPTY_DRAFT: Draft = { name: '', email: '', team: '' };

const EMAIL_TAKEN = 'ENQUIRY_ASSIGNEE_EMAIL_TAKEN';

function draftOf(person: EnquiryAssignee): Draft {
  return { name: person.name, email: person.email, team: person.team ?? '' };
}

/** Routes a write refusal onto the three controls; everything else is form-level. */
function routeErrors(caught: unknown): { fields: FieldErrors; form: string | null } {
  if (!(caught instanceof ApiError)) {
    return {
      fields: {},
      form: caught instanceof Error ? caught.message : 'The change was not saved.',
    };
  }
  const routed = caught.routeFieldErrors(FIELD_KEYS);
  const fields: FieldErrors = { ...routed.fields };
  // The duplicate-email 409 names `email`; mark it even if it arrived without details.
  if (caught.code === EMAIL_TAKEN && !fields.email) {
    fields.email = caught.formMessage ?? caught.message;
  }
  const unroutable = routed.unroutable.filter((message) => message !== fields.email);
  if (unroutable.length > 0) return { fields, form: unroutable.join(' ') };
  if (Object.keys(fields).length === 0) return { fields, form: caught.formMessage ?? caught.message };
  return { fields, form: null };
}

function createBody(draft: Draft): EnquiryAssigneeCreateInput {
  const team = draft.team.trim();
  return {
    name: draft.name.trim(),
    email: draft.email.trim(),
    ...(team ? { team } : {}),
  };
}

/** Only the keys that moved. An empty patch is a 422, so `null` means "nothing to send". */
function patchBody(person: EnquiryAssignee, draft: Draft): EnquiryAssigneeUpdateInput | null {
  const patch: EnquiryAssigneeUpdateInput = {};
  const name = draft.name.trim();
  const email = draft.email.trim();
  const team = draft.team.trim();
  if (name !== person.name) patch.name = name;
  if (email !== person.email) patch.email = email;
  if ((team || null) !== person.team) patch.team = team || null;
  return Object.keys(patch).length > 0 ? patch : null;
}

function ErrorBlock({ title, message }: { title: string; message: string }) {
  return (
    <Notice tone="danger" role="alert" title={title}>
      <span className="min-w-0 [overflow-wrap:anywhere]">{message}</span>
    </Notice>
  );
}

interface DraftFieldsProps {
  draft: Draft;
  errors: FieldErrors;
  disabled: boolean;
  onChange: (next: Draft) => void;
}

function DraftFields({ draft, errors, disabled, onChange }: DraftFieldsProps) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <Field label="Name" error={errors.name} required>
        {(props) => (
          <Input
            {...props}
            value={draft.name}
            maxLength={120}
            disabled={disabled}
            invalid={Boolean(errors.name)}
            onChange={(event) => onChange({ ...draft, name: event.target.value })}
          />
        )}
      </Field>
      <Field label="Email" error={errors.email} required>
        {(props) => (
          <Input
            {...props}
            type="email"
            value={draft.email}
            maxLength={254}
            disabled={disabled}
            invalid={Boolean(errors.email)}
            onChange={(event) => onChange({ ...draft, email: event.target.value })}
          />
        )}
      </Field>
      <Field label="Team" error={errors.team}>
        {(props) => (
          <Input
            {...props}
            value={draft.team}
            maxLength={120}
            disabled={disabled}
            invalid={Boolean(errors.team)}
            onChange={(event) => onChange({ ...draft, team: event.target.value })}
          />
        )}
      </Field>
    </div>
  );
}

export function EnquiryAssigneesCard() {
  const { ability } = useAuth();
  const headingId = useId();
  const denial = denialReason(ability, 'update', 'settings-notifications');
  const canWrite = denial === null;

  const [people, setPeople] = useState<EnquiryAssignee[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [addDraft, setAddDraft] = useState<Draft>(EMPTY_DRAFT);
  const [addErrors, setAddErrors] = useState<FieldErrors>({});
  const [addFormError, setAddFormError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<Draft>(EMPTY_DRAFT);
  const [editErrors, setEditErrors] = useState<FieldErrors>({});
  const [editFormError, setEditFormError] = useState<string | null>(null);
  const [editSaving, setEditSaving] = useState(false);

  const [removing, setRemoving] = useState<EnquiryAssignee | null>(null);
  const [removeBusy, setRemoveBusy] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const [restoringId, setRestoringId] = useState<number | null>(null);
  const [rowError, setRowError] = useState<{ id: number; message: string } | null>(null);

  // The staff directory is only a quick-fill for the add form, so it is read
  // only for a reader who may add.
  const staff = useUserOptions(canWrite && people !== null);

  const load = useCallback((signal?: AbortSignal) => {
    return api
      .listEnquiryAssignees({}, signal)
      .then((response) => {
        if (signal?.aborted) return;
        setPeople(response.data);
        setLoadError(null);
      })
      .catch((caught: unknown) => {
        if (signal?.aborted) return;
        if (caught instanceof ApiError && caught.status === 403) {
          setForbidden(true);
          return;
        }
        if (caught instanceof ApiError && caught.status === 401) return;
        setLoadError(caught instanceof Error ? caught.message : 'The list could not be read.');
      })
      .finally(() => {
        if (!signal?.aborted) setLoading(false);
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const staffOptions = useMemo<ComboboxOption[]>(
    () =>
      staff.users.map((user) => ({
        value: String(user.id),
        label: user.name.trim() || user.email,
        hint: user.email,
      })),
    [staff.users],
  );

  function quickFill(value: string) {
    const user = staff.users.find((candidate) => String(candidate.id) === value);
    if (!user) return;
    setAddDraft((current) => ({ ...current, name: user.name.trim(), email: user.email }));
    setAddErrors({});
  }

  function add() {
    if (adding || !canWrite) return;
    const body = createBody(addDraft);
    if (!body.name || !body.email) return;
    setAdding(true);
    setAddErrors({});
    setAddFormError(null);
    api
      .createEnquiryAssignee(body)
      .then(() => {
        setAddDraft(EMPTY_DRAFT);
        return load();
      })
      .catch((caught: unknown) => {
        const routed = routeErrors(caught);
        setAddErrors(routed.fields);
        setAddFormError(routed.form);
      })
      .finally(() => setAdding(false));
  }

  function startEdit(person: EnquiryAssignee) {
    setEditingId(person.id);
    setEditDraft(draftOf(person));
    setEditErrors({});
    setEditFormError(null);
    setRowError(null);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditErrors({});
    setEditFormError(null);
  }

  function saveEdit(person: EnquiryAssignee) {
    if (editSaving) return;
    const patch = patchBody(person, editDraft);
    if (!patch) return;
    setEditSaving(true);
    setEditErrors({});
    setEditFormError(null);
    api
      .updateEnquiryAssignee(person.id, patch)
      .then(() => {
        setEditingId(null);
        return load();
      })
      .catch((caught: unknown) => {
        const routed = routeErrors(caught);
        setEditErrors(routed.fields);
        setEditFormError(routed.form);
      })
      .finally(() => setEditSaving(false));
  }

  function confirmRemove() {
    if (!removing || removeBusy) return;
    setRemoveBusy(true);
    setRemoveError(null);
    api
      .updateEnquiryAssignee(removing.id, { isActive: false })
      .then(() => {
        setRemoving(null);
        return load();
      })
      .catch((caught: unknown) => {
        setRemoveError(
          caught instanceof ApiError
            ? (caught.formMessage ?? caught.message)
            : caught instanceof Error
              ? caught.message
              : 'The person was not removed.',
        );
      })
      .finally(() => setRemoveBusy(false));
  }

  function restore(person: EnquiryAssignee) {
    if (restoringId !== null) return;
    setRestoringId(person.id);
    setRowError(null);
    api
      .updateEnquiryAssignee(person.id, { isActive: true })
      .then(() => load())
      .catch((caught: unknown) => {
        setRowError({
          id: person.id,
          message:
            caught instanceof ApiError
              ? (caught.formMessage ?? caught.message)
              : caught instanceof Error
                ? caught.message
                : 'The person was not restored.',
        });
      })
      .finally(() => setRestoringId(null));
  }

  const addBody = createBody(addDraft);
  const addMissing = !addBody.name || !addBody.email;
  const addReason = denial ?? (addMissing ? 'Name and email are required.' : null);

  return (
    <Panel aria-labelledby={headingId}>
      <PanelHeader id={headingId} title="Enquiry assignees" />
      <PanelBody className="flex flex-col gap-4">
        {loadError && <ErrorBlock title="Couldn’t read the assignee list." message={loadError} />}

        {loading && people === null && !forbidden ? (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        ) : forbidden ? (
          <p className="m-0 text-xs text-fg-subtle">
            Your permission set does not include this. An administrator can change that.
          </p>
        ) : people === null ? null : (
          <>
            {people.length === 0 ? (
              <p className="m-0 text-xs text-fg-subtle">Nobody is on the list.</p>
            ) : (
              <ul className="m-0 flex list-none flex-col divide-y divide-border p-0">
                {people.map((person) => {
                  const editing = editingId === person.id;
                  if (editing) {
                    const patch = patchBody(person, editDraft);
                    return (
                      <li key={person.id} className="flex flex-col gap-3 py-3">
                        {editFormError && (
                          <ErrorBlock title="Couldn’t save the change." message={editFormError} />
                        )}
                        <DraftFields
                          draft={editDraft}
                          errors={editErrors}
                          disabled={editSaving}
                          onChange={setEditDraft}
                        />
                        <div className="flex flex-wrap items-center gap-2">
                          <Button
                            onClick={() => saveEdit(person)}
                            disabled={editSaving || patch === null || !editDraft.name.trim() || !editDraft.email.trim()}
                          >
                            {editSaving ? 'Saving…' : 'Save'}
                          </Button>
                          <Button variant="ghost" onClick={cancelEdit} disabled={editSaving}>
                            Cancel
                          </Button>
                        </div>
                      </li>
                    );
                  }
                  return (
                    <li
                      key={person.id}
                      className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="flex min-w-0 flex-col gap-0.5">
                        <div className="flex flex-wrap items-center gap-2">
                          <span
                            className={
                              person.isActive
                                ? 'text-sm font-medium text-fg'
                                : 'text-sm font-medium text-fg-subtle'
                            }
                          >
                            {person.name}
                          </span>
                          {person.team && (
                            <Badge tone="neutral" variant="outline">
                              {person.team}
                            </Badge>
                          )}
                          {!person.isActive && <Badge tone="warning">Removed</Badge>}
                        </div>
                        <span className="text-xs [overflow-wrap:anywhere]">
                          <EmailLink address={person.email} tone="muted" />
                        </span>
                        {rowError?.id === person.id && (
                          <p role="alert" className="m-0 text-xs text-danger">
                            {rowError.message}
                          </p>
                        )}
                      </div>
                      {canWrite && (
                        <div className="flex shrink-0 flex-wrap items-center gap-2">
                          {person.isActive ? (
                            <>
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => startEdit(person)}
                                disabled={editingId !== null}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setRemoveError(null);
                                  setRemoving(person);
                                }}
                              >
                                Remove
                              </Button>
                            </>
                          ) : (
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => restore(person)}
                              loading={restoringId === person.id}
                              disabled={restoringId !== null && restoringId !== person.id}
                            >
                              Restore
                            </Button>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}

            <div className="flex flex-col gap-3 border-t border-border pt-4">
              {addFormError && <ErrorBlock title="Couldn’t add the person." message={addFormError} />}
              {canWrite && (
                <Field label="From EMERGE users">
                  {({ id, 'aria-describedby': describedBy }) => (
                    <Combobox
                      id={id}
                      aria-describedby={describedBy}
                      value=""
                      onChange={quickFill}
                      options={staffOptions}
                      placeholder="Choose a user…"
                      searchPlaceholder="Search staff…"
                      loading={staff.loading}
                      disabled={adding}
                      {...(staff.failed
                        ? { footnote: 'The staff list could not be read.' }
                        : staff.hasMore
                          ? { footnote: 'Showing the first page of staff.' }
                          : {})}
                    />
                  )}
                </Field>
              )}
              <DraftFields
                draft={addDraft}
                errors={addErrors}
                disabled={!canWrite || adding}
                onChange={(next) => {
                  setAddDraft(next);
                  setAddFormError(null);
                }}
              />
              <div className="flex flex-wrap items-center gap-3">
                {/* The `title` is on a WRAPPER: a disabled `Button` never receives the hover. */}
                <span title={addReason ?? undefined}>
                  <Button onClick={add} disabled={addReason !== null || adding}>
                    {adding ? 'Adding…' : 'Add person'}
                  </Button>
                </span>
                {denial && <p className="m-0 text-xs text-fg-subtle">{denial}</p>}
              </div>
            </div>
          </>
        )}
      </PanelBody>

      <ConfirmDialog
        open={removing !== null}
        onClose={() => {
          if (!removeBusy) setRemoving(null);
        }}
        onConfirm={confirmRemove}
        title={removing ? `Remove ${removing.name}?` : 'Remove this person?'}
        description={
          removing
            ? `Enquiries already assigned to ${removing.name} keep the name. ${removing.name} stops being offered when assigning, and can be restored from this list.`
            : ''
        }
        confirmLabel="Remove"
        loading={removeBusy}
        error={removeError ?? undefined}
      />
    </Panel>
  );
}
